import React, { useState } from 'react'
import { FileText, Eye, Code, Copy, Check } from 'lucide-react'
import ArtifactStateOverlay from './ArtifactStateOverlay'

interface SddViewerProps {
  markdown?: string
  agentStatus?: string
  darkMode?: boolean
  isRegenerating?: boolean
  stale?: boolean
  onReviewAdr?: () => void
  onOpenDiagrams?: () => void
}

function cleanMarkdown(raw: string): string {
  // Remove ```markdown or ``` at the beginning
  let cleaned = raw
    .replace(/^```markdown\s*/i, '')
    .replace(/^```\s*/i, '')
    .trim()
  // Remove trailing ```
  cleaned = cleaned.replace(/\s*```\s*$/i, '').trim()
  return cleaned
}

export default function SddViewer({ markdown, isRegenerating = false, stale = false, onReviewAdr, onOpenDiagrams }: SddViewerProps) {
  const [mode, setMode] = useState<'preview' | 'code'>('preview')
  const [copied, setCopied] = useState(false)
  const hasSdd = !!markdown && !markdown.startsWith('[ERRO]')

  const handleCopy = async () => {
    if (!markdown) return
    await navigator.clipboard.writeText(markdown)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">SDD</h3>
          {hasSdd && !isRegenerating && (
            <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium">
              <FileText size={10} />
              Documento gerado
            </span>
          )}
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            <button
              onClick={() => setMode('preview')}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                mode === 'preview'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <Eye size={12} />
              Preview
            </button>
            <button
              onClick={() => setMode('code')}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                mode === 'code'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <Code size={12} />
              Markdown
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasSdd && onOpenDiagrams && (
            <button
              type="button"
              onClick={onOpenDiagrams}
              className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium border border-line text-ink cursor-pointer"
            >
              Ver diagramas
            </button>
          )}
          {mode === 'code' && hasSdd && (
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copiado' : 'Copiar'}
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden relative">
        {mode === 'preview' ? (
          <div className="flex-1 overflow-auto bg-canvas">
            {!hasSdd ? (
              <div className="text-center text-faint py-16">
                <FileText size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhum SDD gerado ainda.</p>
                <p className="text-xs mt-1">Marque pelo menos uma ideia como pronta para o time de agentes analisar.</p>
              </div>
            ) : (
              <SddDocument markdown={markdown || ''} stale={stale} onReviewAdr={onReviewAdr} />
            )}
          </div>
        ) : (
          <div className="flex-1 p-4 bg-gray-50 dark:bg-gray-900">
            <textarea
              value={markdown || ''}
              readOnly
              spellCheck={false}
              placeholder="O SDD gerado pelo agente aparecerá aqui..."
              className="w-full h-full px-3 py-2 text-xs font-mono bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg outline-none focus:ring-2 focus:ring-agent resize-none text-gray-800 dark:text-gray-200 custom-scrollbar placeholder:text-gray-400"
            />
          </div>
        )}

        <ArtifactStateOverlay
          isRegenerating={isRegenerating}
          hasContent={hasSdd}
          label="Gerando o Software Design Document"
          hint="A síntese arquitetural está sendo redigida pelo agente."
        />
      </div>
    </div>
  )
}

function SddDocument({ markdown, stale, onReviewAdr }: { markdown: string; stale: boolean; onReviewAdr?: () => void }) {
  const cleaned = cleanMarkdown(markdown)
  const outline = cleaned
    .split('\n')
    .map((line) => line.match(/^(#{1,3})\s+(.+)/))
    .filter((match): match is RegExpMatchArray => Boolean(match))
    .map((match) => ({ level: match[1].length, title: match[2].replace(/\*\*/g, '') }))
  const adrBlocks = cleaned.split(/\n(?=###?\s+)/).filter((block) => /adr[- ]?\d+/i.test(block) || /pendente hitl/i.test(block))
  const notes = adrBlocks
    .map((block) => {
      const title = block.split('\n')[0].replace(/^#+\s*/, '').trim()
      const pending = /pendente hitl/i.test(block)
      return { title, pending, text: block.split('\n').slice(1, 4).join(' ').replace(/\*\*/g, '').trim() }
    })
    .slice(0, 6)

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[190px_minmax(0,720px)_260px] gap-6 p-5 justify-center items-start">
      <nav className="hidden xl:flex flex-col gap-0.5 sticky top-0">
        <span className="font-mono text-[10.5px] uppercase tracking-wide text-faint px-2 pb-2">Sumário</span>
        {outline.map((item, index) => (
          <div key={`${item.title}-${index}`} className="flex items-center gap-2 px-2 py-1 rounded-md text-[12.5px] text-muted" style={{ paddingLeft: 8 + (item.level - 1) * 8 }}>
            <span className="font-mono text-[11px] text-faint w-4">{index + 1}</span>
            <span className="truncate">{item.title}</span>
          </div>
        ))}
      </nav>
      <article className="bg-surface border border-line rounded-[10px] px-8 py-7">
        <div className="flex items-center gap-2 font-mono text-[11px] text-faint mb-4">
          <span>SDD</span>
          <span>·</span>
          <span className={stale ? 'text-warn' : 'text-ok'}>{stale ? 'Desatualizado · há ideias novas' : 'Atualizado'}</span>
        </div>
        <div className="prose dark:prose-invert prose-sm max-w-none">
          <SddMarkdown content={markdown} />
        </div>
      </article>
      <aside className="hidden xl:flex flex-col gap-2.5 sticky top-0">
        <span className="font-mono text-[10.5px] uppercase tracking-wide text-faint">Decisões e notas</span>
        {notes.length === 0 && <p className="text-xs text-faint">As notas dos ADRs aparecem aqui depois da síntese.</p>}
        {notes.map((note) => (
          <div key={note.title} className="bg-surface border border-line rounded-lg p-2.5 shadow-[inset_3px_0_0_var(--af-acc)]">
            <div className="text-[12.5px] font-semibold">{note.title}</div>
            {note.text && <p className="text-xs text-muted mt-1 leading-relaxed">{note.text.slice(0, 220)}</p>}
            {note.pending && onReviewAdr && (
              <button type="button" onClick={onReviewAdr} className="mt-2 h-6 px-2 rounded-md bg-warn text-white text-[11.5px] font-semibold cursor-pointer">
                Revisar
              </button>
            )}
          </div>
        ))}
      </aside>
    </div>
  )
}

function SddMarkdown({ content }: { content: string }) {
  const cleaned = cleanMarkdown(content)
  const lines = cleaned.split('\n')
  const elements: React.ReactNode[] = []
  let key = 0

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()

    if (trimmed.startsWith('# ')) {
      elements.push(
        <h1 key={key++} className="text-2xl font-bold text-gray-900 dark:text-white mb-4 mt-2">
          {trimmed.slice(2)}
        </h1>
      )
    } else if (trimmed.startsWith('## ')) {
      elements.push(
        <h2 key={key++} className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3 mt-6 pb-1 border-b border-gray-200 dark:border-gray-700">
          {trimmed.slice(3)}
        </h2>
      )
    } else if (trimmed.startsWith('### ')) {
      elements.push(
        <h3 key={key++} className="text-base font-medium text-gray-700 dark:text-gray-200 mb-2 mt-4">
          {trimmed.slice(4)}
        </h3>
      )
    } else if (trimmed.startsWith('#### ')) {
      elements.push(
        <h4 key={key++} className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2 mt-3">
          {trimmed.slice(5)}
        </h4>
      )
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      elements.push(
        <ul key={key++} className="list-disc ml-5 mb-1">
          <li className="text-sm text-gray-700 dark:text-gray-300">{trimmed.slice(2)}</li>
        </ul>
      )
    } else if (trimmed.match(/^\d+\.\s/)) {
      elements.push(
        <ol key={key++} className="list-decimal ml-5 mb-1">
          <li className="text-sm text-gray-700 dark:text-gray-300">{trimmed.replace(/^\d+\.\s/, '')}</li>
        </ol>
      )
    } else if (trimmed.startsWith('```')) {
      const lang = trimmed.slice(3).trim()
      let code = ''
      i++
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        code += lines[i] + '\n'
        i++
      }
      elements.push(
        <pre key={key++} className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg overflow-x-auto my-3">
          {lang && <code className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{lang}</code>}
          <code className="text-xs font-mono text-gray-800 dark:text-gray-200 block mt-1">{code}</code>
        </pre>
      )
    } else if (trimmed === '') {
      elements.push(<div key={key++} className="h-2" />)
    } else if (trimmed.startsWith('> ')) {
      elements.push(
        <blockquote key={key++} className="border-l-4 border-agent pl-3 py-1 my-2 text-sm italic text-gray-600 dark:text-gray-400">
          {trimmed.slice(2)}
        </blockquote>
      )
    } else if (trimmed.startsWith('---') || trimmed.startsWith('***')) {
      elements.push(<hr key={key++} className="my-4 border-gray-200 dark:border-gray-700" />)
    } else {
      // Bold and italic inline
      let text = trimmed
      text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      text = text.replace(/\*(.*?)\*/g, '<em>$1</em>')
      text = text.replace(/`(.*?)`/g, '<code class="px-1 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-xs font-mono">$1</code>')
      elements.push(
        <p key={key++} className="text-sm text-gray-700 dark:text-gray-300 mb-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: text }} />
      )
    }
  }

  return <>{elements}</>
}
